import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';
import { useAuth } from '../../contexts/AuthContext';

// Decks tab component
export const DecksTab = ({ decks, users, onEditDeck, onDeleteDeck, onRowClick }) => {
  const [filteredDecks, setFilteredDecks] = useState(decks);
  const [searchTerm, setSearchTerm] = useState("");
  const [deckTypeFilter, setDeckTypeFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const decksPerPage = 20;

  React.useEffect(() => {
    let result = [...decks];

    // Apply deck type filter
    if (deckTypeFilter !== "all") {
      result = result.filter(deck =>
        deckTypeFilter === "system" ? deck.is_system_deck : !deck.is_system_deck
      );
    }

    // Apply owner filter
    if (ownerFilter) {
      result = result.filter(deck => deck.owner_id === parseInt(ownerFilter));
    }

    // Apply search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(deck => {
        const translations = deck.translations || {};
        return Object.values(translations).some(trans =>
          trans.name?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === "created_at" || sortField === "updated_at") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredDecks(result);
    setCurrentPage(1);
  }, [decks, deckTypeFilter, ownerFilter, searchTerm, sortField, sortDirection]);

  const indexOfLastDeck = currentPage * decksPerPage;
  const indexOfFirstDeck = indexOfLastDeck - decksPerPage;
  const currentDecks = filteredDecks.slice(indexOfFirstDeck, indexOfLastDeck);
  const totalPages = Math.ceil(filteredDecks.length / decksPerPage);

  const handleSort = useCallback((field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  }, [sortField, sortDirection]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return "↕️";
    return sortDirection === "asc" ? "↑" : "↓";
  };

  const getOwnerName = (ownerId) => {
    const user = users.find(u => u.user_id === ownerId);
    return user ? user.nickname : `User ${ownerId}`;
  };

  return (
      <div>
        <h2>Decks</h2>
        <div style={{ marginBottom: "20px", display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button className="small-action-btn add" onClick={() => onRowClick && onRowClick('decks', { deck_id: -1, isNew: true })}>
            Add Deck
          </button>
          <input
          type="text"
          placeholder="Search by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginRight: "10px", padding: "5px" }}
        />
        <select
          value={deckTypeFilter}
          onChange={(e) => setDeckTypeFilter(e.target.value)}
          style={{ marginRight: "10px", padding: "5px" }}
        >
          <option value="all">All Types</option>
          <option value="system">System Decks</option>
          <option value="user">User Decks</option>
        </select>
        <select
          value={ownerFilter}
          onChange={(e) => setOwnerFilter(e.target.value)}
          style={{ padding: "5px" }}
        >
          <option value="">All Owners</option>
          {users.sort((a, b) => a.nickname.localeCompare(b.nickname)).map(user => (
            <option key={user.user_id} value={user.user_id}>
              {user.nickname}
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: "10px" }}>
        Showing {indexOfFirstDeck + 1}-{Math.min(indexOfLastDeck, filteredDecks.length)} of {filteredDecks.length} decks
      </div>

      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Symbol</th>
            <th>Extension</th>
            <th>Main Deck</th>
            <th>Public</th>
            <th>Type</th>
            <th onClick={() => handleSort("owner_id")} style={{ cursor: "pointer" }}>
              Owner <SortIcon field="owner_id" />
            </th>
            <th onClick={() => handleSort("card_count")} style={{ cursor: "pointer" }}>
              Cards <SortIcon field="card_count" />
            </th>
            <th>Translations</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
                  {currentDecks.map((deck) => (
                    <tr key={deck.deck_id} onClick={() => onRowClick && onRowClick('decks', deck)} style={{ cursor: 'pointer' }}>
                      <td>{deck.deck_id}</td>
                      <td>
                        {deck.deck_symbol_path ? (
                          <img src={deck.deck_symbol_path} alt="Symbol" style={{ width: 32, height: 32, objectFit: 'contain' }} />
                        ) : (
                          <span style={{ color: '#aaa' }}>No symbol</span>
                        )}
                      </td>
                      <td>{deck.is_extension ? "Yes" : "No"}</td>
                      <td>
                        {deck.is_extension && deck.main_deck_id ? (
                          (() => {
                            const mainDeck = decks.find(d => d.deck_id === deck.main_deck_id);
                            if (mainDeck) {
                              const deckName = mainDeck.translations?.en?.name ||
                                Object.values(mainDeck.translations || {})[0]?.name ||
                                `Deck ${mainDeck.deck_id}`;
                              return deckName;
                            }
                            return `Deck ${deck.main_deck_id}`;
                          })()
                        ) : (
                          <span style={{ color: '#aaa' }}>-</span>
                        )}
                      </td>
                      <td>{deck.is_public ? "Yes" : "No"}</td>
                      <td>{deck.is_system_deck ? "System" : "User"}</td>
                      <td>{getOwnerName(deck.owner_id)}</td>
                      <td>{deck.card_count}</td>
                      <td>
                        {deck.translations &&
                          Object.entries(deck.translations).map(([lang, trans]) => (
                            <div key={lang}>
                              <b>{lang}:</b> {trans.name}
                            </div>
                          ))}
                      </td>
                      <td>
                        <button className="small-action-btn edit" onClick={(e) => { e.stopPropagation(); onEditDeck(deck); }}>Edit</button>
                        <button className="small-action-btn delete" onClick={(e) => { e.stopPropagation(); onDeleteDeck(deck.deck_id); }}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
      </table>

      {totalPages > 1 && (
        <div style={{ marginTop: "20px" }}>
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span style={{ margin: "0 10px" }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

// Add Deck Form
export function AddDeckForm({ onAdd, onCancel, languages, users, decks, symbols }) {
  const { user } = useAuth();
  const [isPublic, setIsPublic] = useState(false);
  const [isExtension, setIsExtension] = useState(false);
  const [isSystemDeck, setIsSystemDeck] = useState(true);
  const [mainDeckId, setMainDeckId] = useState("");
  const [deckSymbolId, setDeckSymbolId] = useState("");
  const [ownerId, setOwnerId] = useState(user?.user_id || "");
  const [translations, setTranslations] = useState({});
  const [uploading, setUploading] = useState(false);

  const handleTranslationChange = (langCode, field, value) => {
    setTranslations(prev => ({
      ...prev,
      [langCode]: {
        ...prev[langCode],
        [field]: value,
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!deckSymbolId) {
      alert("Deck symbol is required");
      return;
    }

    if (!ownerId) {
      alert("Owner is required");
      return;
    }

    // Check if at least one language has a name
    const hasAnyTranslation = Object.values(translations).some(trans => trans && trans.name);
    if (!hasAnyTranslation) {
      alert("At least one language must have a name");
      return;
    }

    setUploading(true);
    try {
      await onAdd({
        owner_id: parseInt(ownerId),
        is_system_deck: isSystemDeck,
        is_public: isPublic,
        is_extension: isExtension,
        main_deck_id: mainDeckId ? parseInt(mainDeckId) : null,
        deck_symbol_id: deckSymbolId ? parseInt(deckSymbolId) : null,
        translations: translations,
      });
    } catch (err) {
      alert(err.message || "Failed to add deck");
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginBottom: "20px", padding: "20px", border: "1px solid #ccc", width: '100%', borderRadius: "20px" }}>
      <h3>Add Deck</h3>

      <div style={{ marginBottom: "10px" }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          Type:
        </label>
        <select
          value={isSystemDeck ? "system" : "user"}
          onChange={(e) => setIsSystemDeck(e.target.value === "system")}
          style={{ width: "400px", padding: "5px" }}
        >
          <option value="system">System Deck</option>
          <option value="user">User Deck</option>
        </select>
      </div>

      <div style={{ marginBottom: "10px" }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          Owner:
        </label>
        <select
          value={ownerId}
          onChange={(e) => setOwnerId(e.target.value)}
          style={{ width: "400px", padding: "5px" }}
        >
          <option value="">Select owner...</option>
          {users.sort((a, b) => a.nickname.localeCompare(b.nickname)).map(u => (
            <option key={u.user_id} value={u.user_id}>
              {u.nickname} {u.role === 'admin' ? '(Admin)' : ''}
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: "10px" }}>
        <label>
          <input
            type="checkbox"
            checked={isPublic}
            onChange={(e) => setIsPublic(e.target.checked)}
            style={{ marginRight: "5px" }}
          />
          Public Deck
        </label>
      </div>

      <div style={{ marginBottom: "10px" }}>
        <label>
          <input
            type="checkbox"
            checked={isExtension}
            onChange={(e) => setIsExtension(e.target.checked)}
            style={{ marginRight: "5px" }}
          />
          Extension Deck
        </label>
      </div>

      {isExtension && (
        <div style={{ marginBottom: "10px" }}>
          <label>Main Deck:</label><br />
          <select
            value={mainDeckId}
            onChange={(e) => setMainDeckId(e.target.value)}
            style={{ width: "400px", marginLeft: "10px" }}
          >
            <option value="">Select main deck...</option>
            {decks.filter(deck => !deck.is_extension).sort((a, b) => (a.translations?.en?.name || `Deck ${a.deck_id}`).localeCompare(b.translations?.en?.name || `Deck ${b.deck_id}`)).map(deck => (
              <option key={deck.deck_id} value={deck.deck_id}>
                {deck.translations?.en?.name || `Deck ${deck.deck_id}`}
              </option>
            ))}
          </select>
        </div>
      )}

      <div style={{ marginBottom: "10px" }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          Deck Symbol:
        </label>
        <select
          value={deckSymbolId}
          onChange={(e) => setDeckSymbolId(e.target.value)}
          style={{ width: "400px", padding: "5px" }}
        >
          <option value="">Select deck symbol...</option>
          {symbols.filter(symbol => symbol.symbol_type === "Deck").sort((a, b) => ((a.translations?.en?.name || a.translations?.en) || `Symbol ${a.symbol_id}`).localeCompare((b.translations?.en?.name || b.translations?.en) || `Symbol ${b.symbol_id}`)).map(symbol => (
            <option key={symbol.symbol_id} value={symbol.symbol_id}>
              {(symbol.translations?.en?.name || symbol.translations?.en) || `Symbol ${symbol.symbol_id}`} ({symbol.symbol_type})
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: "10px" }}>
        <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
          Translations:
        </label>
        <div style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>
          Note: If English is not provided, the system will automatically translate from the first available language to English, then to all other languages.
        </div>
        {languages.map(lang => (
          <div key={lang.code} style={{ marginTop: "10px" }}>
            <h4>{lang.name} ({lang.code})</h4>
            <div style={{ marginLeft: "20px" }}>
              <div style={{ marginBottom: "5px" }}>
                <label style={{ display: 'block', marginBottom: '3px' }}>Name:</label>
                <input
                  type="text"
                  value={translations[lang.code]?.name || ""}
                  onChange={(e) => handleTranslationChange(lang.code, "name", e.target.value)}
                  style={{ width: "400px", marginLeft: "10px" }}
                  placeholder="Optional (at least one language required)"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <button type="submit" disabled={uploading}>Add Deck</button>
      <button type="button" onClick={onCancel} style={{ marginLeft: "10px" }}>Cancel</button>
    </form>
  );
}

// Edit Deck Form
export function EditDeckForm({ deck, onSave, onCancel, languages, users, decks, symbols }) {
  const [translations, setTranslations] = useState({});
  const [isPublic, setIsPublic] = useState(!!deck.is_public);
  const [isExtension, setIsExtension] = useState(!!deck.is_extension);
  const [isSystemDeck, setIsSystemDeck] = useState(!!deck.is_system_deck);
  const [mainDeckId, setMainDeckId] = useState(deck.main_deck_id ? deck.main_deck_id.toString() : "");
  const [deckSymbolId, setDeckSymbolId] = useState(deck.deck_symbol_id ? deck.deck_symbol_id.toString() : "");
  const [ownerId, setOwnerId] = useState(deck.owner_id ? deck.owner_id.toString() : "");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const fetchTranslations = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/api/admin/decks/${deck.deck_id}/translations`);
        const translationsByLang = res.data.reduce((acc, t) => {
          acc[t.language] = t;
          return acc;
        }, {});
        setTranslations(translationsByLang);
      } catch (error) {
        console.error("Failed to fetch deck translations", error);
        alert("Failed to load full deck data. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchTranslations();
  }, [deck.deck_id]);

  const handleTranslationChange = (langCode, field, value) => {
    setTranslations(prev => ({
      ...prev,
      [langCode]: {
        ...prev[langCode],
        [field]: value,
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ownerId) {
      alert("Owner is required");
      return;
    }
    setUploading(true);
    try {
      // Only send non-empty translations
      const filteredTranslations = Object.fromEntries(
        Object.entries(translations).filter(
          ([, t]) => t && (t.name?.trim())
        )
      );

      const saveData = {
        owner_id: parseInt(ownerId),
        is_system_deck: isSystemDeck,
        is_public: isPublic,
        is_extension: isExtension,
        main_deck_id: mainDeckId ? parseInt(mainDeckId) : null,
        deck_symbol_id: deckSymbolId ? parseInt(deckSymbolId) : null,
        translations: filteredTranslations,
      };
      await onSave(saveData);
    } catch (err) {
      alert(err.message || "Failed to update deck");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <div>Loading deck data...</div>;
  }

  return (
    <div>
      <h2>Edit Deck</h2>
      <form onSubmit={handleSubmit} style={{ marginBottom: "20px", padding: "20px", border: "1px solid #ccc", width: '100%', borderRadius: "20px" }}>
        <h3>Edit Deck</h3>

        <div style={{ marginBottom: "10px" }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Type:
          </label>
          <select
            value={isSystemDeck ? "system" : "user"}
            onChange={(e) => setIsSystemDeck(e.target.value === "system")}
            style={{ width: "400px", padding: "5px" }}
          >
            <option value="system">System Deck</option>
            <option value="user">User Deck</option>
          </select>
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Owner:
          </label>
          <select
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
            style={{ width: "400px", padding: "5px" }}
          >
            <option value="">Select owner...</option>
            {users.sort((a, b) => a.nickname.localeCompare(b.nickname)).map(u => (
              <option key={u.user_id} value={u.user_id}>
                {u.nickname} {u.role === 'admin' ? '(Admin)' : ''}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label>
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              style={{ marginRight: "5px" }}
            />
            Public Deck
          </label>
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label>
            <input
              type="checkbox"
              checked={isExtension}
              onChange={(e) => setIsExtension(e.target.checked)}
              style={{ marginRight: "5px" }}
            />
            Extension Deck
          </label>
        </div>

        {isExtension && (
          <div style={{ marginBottom: "10px" }}>
            <label>Main Deck:</label><br />
            <select
              value={mainDeckId}
              onChange={(e) => setMainDeckId(e.target.value)}
              style={{ width: "400px", marginLeft: "10px" }}
            >
              <option value="">Select main deck...</option>
              {decks.filter(d => !d.is_extension && d.deck_id !== deck.deck_id).sort((a, b) => (a.translations?.en?.name || `Deck ${a.deck_id}`).localeCompare(b.translations?.en?.name || `Deck ${b.deck_id}`)).map(d => (
                <option key={d.deck_id} value={d.deck_id}>
                  {d.translations?.en?.name || `Deck ${d.deck_id}`}
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={{ marginBottom: "10px" }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Deck Symbol:
          </label>
          <select
            value={deckSymbolId}
            onChange={(e) => setDeckSymbolId(e.target.value)}
            style={{ width: "400px", padding: "5px" }}
          >
            <option value="">Select deck symbol...</option>
            {symbols.filter(symbol => symbol.symbol_type === "Deck").sort((a, b) => ((a.translations?.en?.name || a.translations?.en) || `Symbol ${a.symbol_id}`).localeCompare((b.translations?.en?.name || b.translations?.en) || `Symbol ${b.symbol_id}`)).map(symbol => (
              <option key={symbol.symbol_id} value={symbol.symbol_id}>
                {(symbol.translations?.en?.name || symbol.translations?.en) || `Symbol ${symbol.symbol_id}`} ({symbol.symbol_type})
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
            Translations:
          </label>
          <div style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>
            Note: If English is not provided, the system will automatically translate from the first available language to English, then to all other languages.
          </div>
          {languages.map(lang => (
            <div key={lang.code} style={{ marginTop: "10px" }}>
              <h4>{lang.name} ({lang.code})</h4>
              <div style={{ marginLeft: "20px" }}>
                <div style={{ marginBottom: "5px" }}>
                  <label style={{ display: 'block', marginBottom: '3px' }}>Name:</label>
                  <input
                    type="text"
                    value={translations[lang.code]?.name || ""}
                    onChange={(e) => handleTranslationChange(lang.code, "name", e.target.value)}
                    style={{ width: "400px", marginLeft: "10px" }}
                    placeholder="Optional (at least one language required)"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <button type="submit" disabled={uploading}>Save Deck</button>
        <button type="button" onClick={onCancel} style={{ marginLeft: "10px" }}>Cancel</button>
      </form>
    </div>
  );
}